import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  ShoppingCart, ScanBarcode, Users, Tag, Award, FileText, 
  ArrowRight, ChevronRight, CheckCircle2, Store, Zap
} from 'lucide-react';
import Retail from '../../assets/Retail.png'; // Assuming you have an image in assets

const CONTAINER = 'w-full max-w-7xl xl:max-w-[95%] 2xl:max-w-[85vw] mx-auto px-6 sm:px-8 lg:px-8';
const THEME_COLOR = '#e11d48'; // Rose theme for retail

/* ============================== PAGE DATA ============================== */

const FEATURES = [
  { title: 'Billing & Invoicing', icon: ShoppingCart, desc: 'Fast and accurate billing processes with customizable invoice generation for retail stores.' },
  { title: 'Barcode Scanning', icon: ScanBarcode, desc: 'Integrated scanning support for quick product lookup, accelerating the checkout process.' },
  { title: 'Customer Management', icon: Users, desc: 'Track customer purchase history and preferences for personalized service and outreach.' },
  { title: 'Offers & Discounts', icon: Tag, desc: 'Easily configure and apply promotional campaigns, seasonal sales, and custom discounts.' },
  { title: 'Loyalty Program', icon: Award, desc: 'Reward repeat customers with integrated points, tiers, and loyalty reward management.' },
  { title: 'Sales Reports', icon: FileText, desc: 'Generate detailed daily, weekly, and monthly sales analytics to track store performance.' }
];

const RetailPOS = () => {
  // Use location hook to get routing state
  const location = useLocation();
  const navSource = location.state?.from === 'products' ? 'products' : 'industries';

  return (
    <div className="overflow-x-hidden pt-0">
      <style>{`
        @keyframes seFadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
        .se-fade-up { animation: seFadeUp .7s ease-out both; }
        @keyframes seFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        .se-float { animation: seFloat 6s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .se-float, .se-fade-up { animation: none !important; } }
      `}</style>

      {/* ================= HERO SECTION ================= */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#fff1f2] via-[#f8fafc] to-white">
        <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-24 w-80 h-80 rounded-full bg-[#e11d48]/10 blur-3xl" />
        <div className={`${CONTAINER} relative py-5 lg:py-6`}>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-8 items-center">
            
            <div className="max-w-2xl relative z-10 se-fade-up">
              
              {/* Conditionally Render Navigation Based on State */}
              <nav className="flex flex-wrap items-center gap-2 text-sm text-slate-500 mb-6">
                <Link to="/" className="hover:text-primary transition-colors">Home</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <Link to={`/${navSource}`} className="hover:text-primary transition-colors capitalize">{navSource}</Link>
                <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                <span className="text-primary font-medium">Retail & POS</span>
              </nav>

              <span className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-primary bg-white ring-1 ring-primary/25 px-4 py-1.5 rounded-full mb-6">
                <ShoppingCart className="w-4 h-4 text-[#e11d48]" /> SmartEdz Retail Solutions
              </span>
              <h1 className="text-4xl sm:text-5xl xl:text-[3.7rem] font-extrabold leading-[1.08] tracking-tight mb-6" style={{ color: '#13294b' }}>
                Advanced POS & Billing for Retail Stores
              </h1>
              <p className="text-lg text-slate-500 max-w-xl leading-relaxed mb-8">
                Transform your retail operations with a lightning-fast point of sale system. Manage inventory, engage customers, and accelerate checkouts across single or multiple locations.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/request-demo" className="inline-flex items-center gap-2 rounded-lg px-7 py-3.5 font-semibold text-white shadow-lg shadow-primary/30 bg-primary hover:brightness-110 transition">
                  Request a Demo <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* Replaced Mockups with Provided Image */}
            <div className="relative se-fade-up w-full" style={{ animationDelay: '.15s' }}>
              <div className="absolute inset-x-8 top-4 bottom-16 bg-gradient-to-tr from-[#e11d48]/15 to-[#fb7185]/15 blur-3xl rounded-[48px]" />
              <img 
                src={Retail} 
                alt="Retail POS Dashboard Illustration" 
                className="relative z-10 w-full h-auto object-contain mix-blend-multiply hover:scale-[1.02] transition-transform duration-500"
              />
            </div>

          </div>
        </div>
      </section>

      {/* ================= CORE MODULES ================= */}
      <section className="bg-gradient-to-b from-[#f5f9fd] to-white py-14">
        <div className={CONTAINER}>
          <div className="text-center mb-10">
            <span className="block text-xs font-bold tracking-[0.2em] uppercase text-primary mb-3">Core Modules</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight" style={{ color: '#13294b' }}>Everything you need in one dashboard</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((feature, index) => (
              <div key={index} className="group h-full flex flex-col bg-white rounded-2xl ring-1 ring-slate-100 p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110" style={{ backgroundColor: `${THEME_COLOR}1A` }}>
                  <feature.icon className="w-6 h-6" style={{ color: THEME_COLOR }} />
                </div>
                <h3 className="font-bold text-slate-800 text-lg mb-3">{feature.title}</h3>
                <p className="text-[13px] text-slate-500 leading-relaxed flex-1">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= INDUSTRY BENEFITS ================= */}
      <section className="py-14 bg-white">
        <div className={CONTAINER}>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="se-fade-up">
              <span className="block text-xs font-bold tracking-[0.2em] uppercase text-[#e11d48] mb-3">
                Why SmartEdz?
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-6" style={{ color: '#13294b' }}>
                Built for rapid checkouts and seamless inventory sync
              </h2>
              <p className="text-slate-500 mb-8 leading-relaxed">
                Slow checkouts and stock discrepancies cost you sales. Our POS platform is engineered to process transactions in seconds while automatically syncing your inventory across all channels.
              </p>
              <div className="space-y-6">
                {[
                  { title: 'Lightning-Fast Checkout', desc: 'Process sales, apply discounts, and accept multiple payment methods instantly to eliminate long queues.', icon: Zap },
                  { title: 'Omnichannel Inventory', desc: 'Sync stock levels across physical stores and e-commerce platforms in real-time to prevent overselling.', icon: ScanBarcode },
                  { title: 'Customer Loyalty Built-In', desc: 'Automatically track purchase history and award loyalty points to drive repeat business seamlessly.', icon: Award }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-[#e11d48]/10 text-[#e11d48]">
                      <item.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800 text-lg">{item.title}</h4>
                      <p className="text-sm text-slate-500 mt-1">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative rounded-2xl bg-slate-50 border border-slate-100 p-8 shadow-inner se-fade-up" style={{ animationDelay: '0.2s' }}>
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#e11d48]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
              <h3 className="font-bold text-slate-800 mb-6 flex items-center gap-2">
                <Store className="w-5 h-5 text-[#e11d48]" /> Store Health
              </h3>
              <div className="space-y-4 relative z-10">
                {['Queues Cleared', 'Inventory Synced', 'Loyalty Rewarded'].map((label, idx) => (
                  <div key={idx} className="bg-white p-4 rounded-xl shadow-sm border border-slate-100 flex items-center justify-between">
                    <span className="font-semibold text-slate-700 text-sm">{label}</span>
                    <span className="flex items-center gap-1 text-xs font-bold text-[#10b981] bg-[#10b981]/10 px-2 py-1 rounded-md">
                      <CheckCircle2 className="w-3 h-3" /> Verified
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= BOTTOM CTA ================= */}
      <section className="py-16">
        <div className={CONTAINER}>
          <div className="relative rounded-[2rem] overflow-hidden bg-gradient-to-br from-[#13294b] to-[#1e447a] p-10 sm:p-16 text-center">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#e11d48]/20 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#e11d48]/10 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3" />
            
            <div className="relative z-10 max-w-2xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">
                Ready to modernize your retail store?
              </h2>
              <p className="text-slate-300 text-lg mb-8">
                Join thousands of retailers who use SmartEdz to speed up checkouts, manage inventory, and boost customer loyalty.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link to="/request-demo" className="inline-flex items-center gap-2 rounded-lg px-8 py-4 font-bold text-[#13294b] bg-white hover:bg-slate-50 transition shadow-lg hover:-translate-y-1">
                  Schedule a Free Demo <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/contact-us" className="inline-flex items-center gap-2 rounded-lg px-8 py-4 font-bold text-white bg-white/10 hover:bg-white/20 transition ring-1 ring-white/20">
                  Talk to Sales
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RetailPOS;